import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  alterarCodigoDepartamentoService,
  listarGruposEmpresaDoUsuario,
} from '~/Services/Service';
import Loading from '~/Components/Loading';
import AuthStorage from '~/Utils/AuthStorage';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  CARACTERISTICA_HOMOLOGADOR,
  CARACTERISTICA_ALTERAR_CODIGO_DEPARTAMENTO,
  MSG_SUCESSO_ALTERACAO_DEPARTAMENTO,
} from '~/constant';
import SelecionarDepartamento from './components/SelecionarDepartamento';
import FormAlteracao from './components/FormAlteracao';
import ModalConfirmacaoAlteracao from './components/ModalConfirmacao';
import { alterarCodigoDepartamentoSchema } from './validationSchema';

// W-0654 — Altera Código Departamento (Ferramenta GES, IRREVERSÍVEL).
// Fonte: UAlteraDepto.pas. Tela única: combo GE -> lookup departamento -> grid -> seleção ->
// form (novo código + observação) -> validar (dry-run) -> modal HARD GATE -> executar.
// ⚠️ Cascata/repoint no backend (repoint-sem-disable, sem NOCHECK/DROP, sem DELETE do antigo).
const AlterarCodigoDepartamento = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [carregandoDepartamentos, setCarregandoDepartamentos] = useState(false);
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState(null);
  const [codigoGrupoEmpresa, setCodigoGrupoEmpresa] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [loading, setLoading] = useState(false);

  const possuiPermissaoAcesso =
    AuthStorage.temCaracteristica(CARACTERISTICA_HOMOLOGADOR) &&
    AuthStorage.temCaracteristica(CARACTERISTICA_ALTERAR_CODIGO_DEPARTAMENTO);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const res = await listarGruposEmpresaDoUsuario();
        if (!ativo) return;
        setGruposEmpresa(Array.isArray(res) ? res : []);
      } catch (err) {
        if (!ativo) return;
        setGruposEmpresa([]);
        toast.error(extrairMensagemErro(err));
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesGe = useMemo(
    () =>
      gruposEmpresa.map((ge) => ({
        value: ge.codigo,
        label: `${ge.codigo} - ${ge.apelido}`,
      })),
    [gruposEmpresa]
  );

  const geLabel = useMemo(() => {
    const opt = opcoesGe.find((o) => o.value === codigoGrupoEmpresa);
    return opt ? opt.label : '';
  }, [opcoesGe, codigoGrupoEmpresa]);

  const buscarDepartamentos = useCallback(async () => {
    if (!codigoGrupoEmpresa) return;
    try {
      setCarregandoDepartamentos(true);
      const res = await alterarCodigoDepartamentoService.listarDepartamentos(
        codigoGrupoEmpresa
      );
      setDepartamentos(Array.isArray(res) ? res : []);
    } catch (err) {
      setDepartamentos([]);
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregandoDepartamentos(false);
    }
  }, [codigoGrupoEmpresa]);

  // Carrega o lookup de departamentos sempre que o GE muda.
  useEffect(() => {
    if (!codigoGrupoEmpresa) {
      setDepartamentos([]);
      return;
    }
    buscarDepartamentos();
  }, [codigoGrupoEmpresa, buscarDepartamentos]);

  const onSelecionarGe = (value) => {
    setCodigoGrupoEmpresa(value);
    setDepartamentos([]);
    setDepartamentoSelecionado(null);
  };

  // Submit do form -> dry-run no backend; se válido abre o modal HARD GATE.
  const handleSubmit = async (values, { setFieldError }) => {
    try {
      setLoading(true);
      const r = await alterarCodigoDepartamentoService.validar({
        codigo_grupo_empresa: Number(values.codigoGrupoEmpresa),
        codigo_departamento_atual: Number(values.codigoDepartamentoAtual),
        codigo_departamento_novo: Number(values.codigoDepartamentoNovo),
      });
      if (r && r.valido === false) {
        setFieldError(
          'codigoDepartamentoNovo',
          r.mensagem_erro || 'Código de departamento inválido.'
        );
        return;
      }
      setPendingValues(values);
      setShowConfirm(true);
    } catch (err) {
      // Guards do backend vêm como 400 inline em codigoDepartamentoNovo.
      setFieldError('codigoDepartamentoNovo', extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmClose = async (confirmado, resetForm) => {
    setShowConfirm(false);
    if (!confirmado || !pendingValues) {
      setPendingValues(null);
      return;
    }
    setLoading(true);
    try {
      const resp = await alterarCodigoDepartamentoService.executar({
        codigo_grupo_empresa: Number(pendingValues.codigoGrupoEmpresa),
        codigo_departamento_atual: Number(
          pendingValues.codigoDepartamentoAtual
        ),
        codigo_departamento_novo: Number(pendingValues.codigoDepartamentoNovo),
        observacao: pendingValues.observacao,
      });
      toast.success(
        (resp && resp.mensagem) || MSG_SUCESSO_ALTERACAO_DEPARTAMENTO
      );
      setPendingValues(null);
      setDepartamentoSelecionado(null);
      if (resetForm) resetForm();
      await buscarDepartamentos();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  if (!possuiPermissaoAcesso) {
    return (
      <div className="container-fluid mt-3">
        <Card>
          <Card.Header>
            <h4 className="mb-0">Altera Código de Departamento</h4>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-0">
              Você não possui permissão para acessar essa funcionalidade.
            </p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '24px 32px',
              borderRadius: 4,
            }}
          >
            <Loading label="Processando alteração de código..." />
          </div>
        </div>
      )}

      <Card>
        <Card.Header>
          <h4 className="mb-0">Altera Código de Departamento</h4>
        </Card.Header>
        <Card.Body>
          <Formik
            enableReinitialize
            initialValues={{
              codigoGrupoEmpresa: codigoGrupoEmpresa || '',
              codigoDepartamentoAtual: departamentoSelecionado
                ? departamentoSelecionado.codigo
                : '',
              codigoDepartamentoNovo: '',
              observacao: '',
            }}
            validationSchema={alterarCodigoDepartamentoSchema}
            onSubmit={handleSubmit}
          >
            {({ values, resetForm, setFieldValue }) => (
              <Form>
                <SelecionarDepartamento
                  opcoesGe={opcoesGe}
                  codigoGrupoEmpresa={codigoGrupoEmpresa}
                  onSelecionarGe={(value) => {
                    onSelecionarGe(value);
                    setFieldValue('codigoGrupoEmpresa', value || '');
                    setFieldValue('codigoDepartamentoAtual', '');
                  }}
                  departamentos={departamentos}
                  carregando={carregandoDepartamentos}
                  departamentoSelecionado={departamentoSelecionado}
                  onSelecionarDepartamento={(depto) => {
                    setDepartamentoSelecionado(depto);
                    setFieldValue('codigoDepartamentoAtual', depto.codigo);
                  }}
                />

                <FormAlteracao
                  departamentoSelecionado={departamentoSelecionado}
                  disabled={loading}
                />

                <Row className="mt-4">
                  <Col
                    xs={12}
                    className="d-flex flex-column flex-md-row justify-content-md-end"
                  >
                    <Button
                      variant="outline-secondary"
                      className="mr-md-2 mb-2 mb-md-0"
                      onClick={() => {
                        resetForm();
                        setDepartamentoSelecionado(null);
                      }}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !departamentoSelecionado}
                    >
                      Alterar Código
                    </Button>
                  </Col>
                </Row>

                <ModalConfirmacaoAlteracao
                  show={showConfirm}
                  onClose={(confirmado) =>
                    handleConfirmClose(confirmado, resetForm)
                  }
                  codigoAntigo={values.codigoDepartamentoAtual}
                  codigoNovo={values.codigoDepartamentoNovo}
                  geLabel={geLabel}
                  descricaoDepartamento={
                    departamentoSelecionado
                      ? departamentoSelecionado.descricao
                      : ''
                  }
                  observacao={values.observacao}
                />
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AlterarCodigoDepartamento;
